"use client";

import { useCallback, useMemo } from "react";
import { AIChat } from "@/components/ai-chat";
import { ACCEPTED_AI_FILE_TYPES } from "@/components/ai-chat/constants";
import type { AIChatAPIFunctions } from "@/components/ai-chat/types";
import { usePatientQAChat } from "@/hooks/use-patient-qa-chat";
import { usePatientTabNavigation } from "@/hooks/use-patient-tab-navigation";
import createId from "@/lib/create-id";
import { formatName } from "@/lib/format-data";
import type { PatientSummaryContext } from "@/types/ai";
import { AIChatType } from "@/types/enums";
import type { SuggestionItem } from "@/types/suggestions";
import { PatientCitationContent } from "./patient-citation-content";
import { generatePatientSuggestions, streamPatientQA } from "@/api/ai/patient";
interface AIQAInterfaceProps {
  context: PatientSummaryContext;
  patientId: string;
  aiSuggestions?: string[] | null;
}
const TAB_MAP: Record<string, string> = {
  demographics: "overview",
  appointments: "appointments",
  "medical-notes": "medical-notes",
  measurements: "overview",
  medications: "overview",
  "forms-documents": "forms-documents"
};
export function AIQAInterface({
  context,
  patientId,
  aiSuggestions
}: AIQAInterfaceProps) {
  const {
    navigateToTab,
    highlightSection
  } = usePatientTabNavigation();
  const mentionSuggestions = useMemo((): SuggestionItem[] => {
    const p = context.patient;
    const label = formatName(p.firstName, p.lastName);
    return [{
      id: p.id,
      label,
      content: label,
      type: "PATIENT"
    }];
  }, [context.patient]);
  const handleCitationClick = useCallback((tab: string, appointmentId?: string) => {
    const targetTab = TAB_MAP[tab] || tab;
    navigateToTab(patientId, targetTab);
    setTimeout(() => {
      if (tab === "appointments" && appointmentId) {
        const el = document.getElementById(`appointment-${appointmentId}`);
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
          el.classList.add("ai-citation-highlight");
          setTimeout(() => el.classList.remove("ai-citation-highlight"), 2500);
        }
      } else if (tab !== "appointments") {
        highlightSection(tab);
      }
    }, 300);
  }, [patientId, navigateToTab, highlightSection]);
  const apiFunctions: AIChatAPIFunctions = useMemo(() => ({
    streamResponse: async function* (question, history, files, signal, modelName) {
      const chatId = createId("ach");
      for await (const chunk of streamPatientQA(context, question, history, files, signal, modelName || "gemini-3.1-flash-lite", patientId, chatId, undefined)) {
        yield chunk;
      }
    },
    generateSuggestions: async history_0 => {
      return generatePatientSuggestions(context, history_0);
    }
  }), [context, patientId]);
  const chat = usePatientQAChat({
    api: apiFunctions,
    chatType: AIChatType.PATIENT_QA,
    initialSuggestions: aiSuggestions || undefined
  });
  return <AIChat.Provider state={chat.state} actions={chat.actions} feedback={chat.feedback}>
      <div className="flex h-full min-h-0 flex-col bg-background">
        <AIChat.Messages className="border-b border-border px-4 py-4 sm:px-6" footer={<AIChat.Footer />}>
          {(msg, idx) => <AIChat.Message message={msg} index={idx}>
              {msg.role === "system" || msg.isSummary ? <AIChat.SummaryDivider message={msg} onSummaryClick={() => {
            chat.setShowSummaryDialog(true);
          }} /> : msg.role === "user" ? <div className="flex flex-col items-end gap-1">
                  <AIChat.Message.Timestamp />
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary/10 px-4 py-2.5">
                    <AIChat.Message.Content />
                  </div>
                </div> : <div className="space-y-2">
                  <PatientCitationContent onCitationClick={handleCitationClick} />
                  <AIChat.Message.SourceLinks />
                  <div className="flex items-center gap-2">
                    <AIChat.Message.FeedbackButtons />
                    <AIChat.Message.Timestamp />
                  </div>
                </div>}
            </AIChat.Message>}
        </AIChat.Messages>

        {!chat.state.isStreaming && <AIChat.Suggestions suggestions={chat.suggestions} isLoading={chat.isGeneratingSuggestions} suggestionKey={chat.suggestionKey} className="mx-3 my-2 sm:mx-4" />}

        <AIChat.Input className="bg-gradient-to-t from-background via-background/80 to-transparent py-2 px-4 rounded-b-md">
          <AIChat.Input.DropZone accept={ACCEPTED_AI_FILE_TYPES} maxSize={10 * 1024 * 1024} className="w-full max-w-2xl mx-auto flex flex-col">
            <AIChat.Input.FilePills />
            <div className="relative flex min-w-full items-center gap-2 rounded-2xl border border-border px-2 py-1.5 shadow-md bg-background/90 backdrop-blur-sm transition-colors duration-300 focus-within:border-primary focus-within:bg-background focus-within:ring-1 focus-within:ring-primary/20">
              <AIChat.Input.TextArea placeholder="Ask a question..." mentionSuggestions={mentionSuggestions} />
              <AIChat.Input.FileUpload />
              <AIChat.Input.CancelButton />
              <AIChat.Input.SendButton />
            </div>
            <div className="mt-1 text-caption text-foreground-muted flex justify-between flex-wrap">
              <span className="text-nowrap">
                Press Enter to send, Shift+Enter for new line
              </span>
              <span className="text-caption text-foreground-muted italic text-right text-nowrap">
                AI can make mistakes. Always verify information.
              </span>
            </div>
          </AIChat.Input.DropZone>
        </AIChat.Input>

        <AIChat.FeedbackDialog />

        {chat.summaryText && <AIChat.SummaryDialog open={chat.showSummaryDialog} onOpenChange={chat.setShowSummaryDialog} summary={chat.summaryText} />}
      </div>
    </AIChat.Provider>;
}